import { Controller } from "@hotwired/stimulus"

// Drives the season form's Car-classes block.
//
// Targets:
//   gameSelect      — the season[game] <select>
//   mixCheckbox     — the season[car_classes_mix] checkbox
//   chipsContainer  — the wrapper that holds the chip multi-select
//   addSelect       — the "+ Add class…" <select> the user picks from
//   chip            — each rendered chip (multiple)
//   chipTemplate    — <template> cloned to render new chips
//   confirmDialog   — the <dialog> warning about a game change losing classes
//   newGameLabel    — the span inside the dialog showing the new game's display name
//   lostClasses     — the chip wrapper inside the dialog listing classes that would be removed
//   emptyHint       — the "Select a game first" hint shown when game is empty
//
// Values:
//   availableByGame — JSON object: { "f1_25": ["F1", "F2", ...], ... }
//   gameLabels      — JSON object: { "f1_25": "F1 25", ... }
export default class extends Controller {
  static targets = [
    "gameSelect",
    "mixCheckbox",
    "chipsContainer",
    "addSelect",
    "chip",
    "chipTemplate",
    "confirmDialog",
    "newGameLabel",
    "lostClasses",
    "emptyHint",
    "classOption",
    "picker",
  ]
  static values = {
    availableByGame: Object,
    gameLabels: Object,
  }

  connect() {
    this.previousGame = this.hasGameSelectTarget
      ? this.gameSelectTarget.value
      : ""
    this.refreshDisabled()
    this.refreshAddSelectOptions()
  }

  // -- Mix toggle -----------------------------------------------------

  toggleMix() {
    this.refreshDisabled()
  }

  refreshDisabled() {
    const mixOn = this.hasMixCheckboxTarget && this.mixCheckboxTarget.checked
    const gameEmpty = !this.hasGameSelectTarget || !this.gameSelectTarget.value
    const disable = mixOn || gameEmpty

    if (this.hasChipsContainerTarget) {
      this.chipsContainerTarget.classList.toggle("opacity-50", disable)
      this.chipsContainerTarget.classList.toggle("pointer-events-none", disable)
    }
    if (this.hasEmptyHintTarget) {
      this.emptyHintTarget.classList.toggle("hidden", !gameEmpty || mixOn)
    }
  }

  // -- Game change ---------------------------------------------------

  gameChanged(event) {
    const newGame = event.target.value
    const lost = this.classesNotIn(newGame)

    if (lost.length === 0) {
      this.previousGame = newGame
      this.refreshDisabled()
      this.refreshAddSelectOptions()
      return
    }

    this.pendingGame = newGame
    this.pendingLost = lost
    this.populateConfirmDialog(newGame, lost)
    this.confirmDialogTarget.showModal()
  }

  confirmGameChange() {
    this.pendingLost.forEach((cls) => this.removeChipByValue(cls))
    this.previousGame = this.pendingGame
    this.confirmDialogTarget.close()
    this.refreshDisabled()
    this.refreshAddSelectOptions()
  }

  cancelGameChange() {
    if (this.hasGameSelectTarget) {
      this.gameSelectTarget.value = this.previousGame
      // Announced, not dispatched as `change`: re-firing change would re-enter
      // gameChanged and reopen this dialog. Anything mirroring the value — the
      // game tiles in season setup — listens for this instead, otherwise the
      // tiles keep showing the game the organizer just backed out of.
      this.dispatch("gameReverted", { detail: { game: this.previousGame } })
    }
    this.confirmDialogTarget.close()
  }

  backdropCancel(event) {
    if (event.target === this.confirmDialogTarget) {
      this.cancelGameChange()
    }
  }

  // -- Chip operations -----------------------------------------------

  // The combobox path (season setup). `addClass` below is the <select> path
  // the calendar's create sheet still uses — both end at appendChip.
  addValue(event) {
    const value = event.detail?.value
    if (!value || this.currentClasses().includes(value)) return
    this.appendChip(value)
    this.refreshAddSelectOptions()
  }

  addClass() {
    const select = this.addSelectTarget
    const value = select.value
    if (!value) return

    if (this.currentClasses().includes(value)) {
      select.value = ""
      return
    }

    this.appendChip(value)
    select.value = ""
    this.refreshAddSelectOptions()
  }

  removeClass(event) {
    const chip = event.currentTarget.closest(
      "[data-car-classes-form-target~='chip']",
    )
    if (chip) chip.remove()
    this.refreshAddSelectOptions()
  }

  // -- Helpers --------------------------------------------------------

  currentClasses() {
    return this.chipTargets.map((c) => c.dataset.value)
  }

  classesNotIn(game) {
    const allowed = this.availableByGameValue[game] || []
    return this.currentClasses().filter((c) => !allowed.includes(c))
  }

  appendChip(value) {
    if (!this.hasChipTemplateTarget) return

    const html = this.chipTemplateTarget.innerHTML.replaceAll("%VALUE%", value)
    const wrapper = document.createElement("div")
    wrapper.innerHTML = html.trim()
    const node = wrapper.firstChild
    // Chips sit before whichever picker this surface uses: the <select> in the
    // calendar sheet, the combobox in setup. Without the fallback this threw on
    // the missing addSelect target and the chip never landed.
    const anchor = this.hasAddSelectTarget
      ? this.addSelectTarget
      : this.hasPickerTarget
        ? this.pickerTarget
        : null
    anchor
      ? this.chipsContainerTarget.insertBefore(node, anchor)
      : this.chipsContainerTarget.appendChild(node)
  }

  removeChipByValue(value) {
    const chip = this.chipTargets.find((c) => c.dataset.value === value)
    if (chip) chip.remove()
  }

  populateConfirmDialog(newGame, lost) {
    if (this.hasNewGameLabelTarget) {
      this.newGameLabelTarget.textContent =
        this.gameLabelsValue[newGame] || newGame
    }
    if (this.hasLostClassesTarget) {
      this.lostClassesTarget.innerHTML = lost
        .map(
          (c) =>
            `<span class="inline-block px-2 py-1 rounded-md bg-app-surface-active text-[12px] text-app-text-secondary mr-2 mb-1">${c}</span>`,
        )
        .join("")
    }
  }

  refreshAddSelectOptions() {
    this.refreshClassOptions()
    if (!this.hasAddSelectTarget || !this.hasGameSelectTarget) return
    const allowed = this.availableByGameValue[this.gameSelectTarget.value] || []
    const current = this.currentClasses()
    const options = ['<option value="">+ Add class…</option>'].concat(
      allowed
        .filter((c) => !current.includes(c))
        .map((c) => `<option value="${c}">${c}</option>`),
    )
    this.addSelectTarget.innerHTML = options.join("")
  }

  // Setup renders every class for every game as a combobox option and gates
  // them here, rather than re-rendering the list on each game change. The flag
  // is separate from `hidden` because the combobox's own text filter owns that
  // class and the two would fight.
  refreshClassOptions() {
    if (!this.hasClassOptionTarget || !this.hasGameSelectTarget) return
    const allowed = this.availableByGameValue[this.gameSelectTarget.value] || []
    const current = this.currentClasses()
    this.classOptionTargets.forEach((option) => {
      const value = option.dataset.comboboxOptionValue
      option.dataset.gameOk = String(
        allowed.includes(value) && !current.includes(value),
      )
    })
    this.dispatch("optionsChanged")
  }
}
